import { z } from "zod";

import {
  q1Codes,
  q2Codes,
  q3Codes,
  q4Codes,
  recommendationCodes,
} from "../lib/intake-schema";

const contentIdSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Gebruik een stabiele kebab-case ID.");
const shortTextSchema = z.string().trim().min(1).max(180);
const bodyTextSchema = z.string().trim().min(1).max(800);
const httpsUrlSchema = z
  .string()
  .url()
  .refine((url) => url.startsWith("https://"), "Gebruik een https-link.");

function uniqueCodes(
  options: readonly { code: string }[],
  context: z.RefinementCtx,
) {
  const codes = options.map(({ code }) => code);
  if (new Set(codes).size !== codes.length) {
    context.addIssue({
      code: "custom",
      message: "Antwoordcodes moeten uniek zijn binnen een vraag.",
    });
  }
}

function uniqueIds(
  items: readonly { id: string }[],
  context: z.RefinementCtx,
) {
  const ids = items.map(({ id }) => id);
  if (new Set(ids).size !== ids.length) {
    context.addIssue({
      code: "custom",
      message: "Content-IDs moeten uniek zijn binnen een collectie.",
    });
  }
}

/**
 * A fixed-length list keyed by a literal id, checked for completeness. Used
 * wherever the Studio offers a list whose members the code addresses by name:
 * the editor may reword every entry but never add, drop or rename one.
 */
function keyedList<
  Ids extends readonly [string, ...string[]],
  Shape extends z.ZodRawShape,
>(ids: Ids, shape: Shape, message: string) {
  return z
    .array(z.object({ id: z.enum(ids), ...shape }))
    .length(ids.length)
    .superRefine((entries, context) => {
      // The element type is generic here, so the id has to be reasserted; the
      // schema above is what actually guarantees it is present and in `ids`.
      const seen = (entries as readonly { id: string }[]).map(({ id }) => id);
      if (new Set(seen).size !== ids.length) {
        context.addIssue({ code: "custom", message });
      }
    });
}

const questionTextShape = {
  eyebrow: shortTextSchema,
  title: shortTextSchema,
  prompt: shortTextSchema,
};

const q1OptionsSchema = z
  .array(z.object({ code: z.enum(q1Codes), label: bodyTextSchema }))
  .length(q1Codes.length)
  .superRefine(uniqueCodes);
const q2OptionsSchema = z
  .array(z.object({ code: z.enum(q2Codes), label: bodyTextSchema }))
  .length(q2Codes.length)
  .superRefine(uniqueCodes);
const q3OptionsSchema = z
  .array(z.object({ code: z.enum(q3Codes), label: bodyTextSchema }))
  .length(q3Codes.length)
  .superRefine(uniqueCodes);
const q4OptionsSchema = z
  .array(z.object({ code: z.enum(q4Codes), label: bodyTextSchema }))
  .length(q4Codes.length)
  .superRefine(uniqueCodes);

export const soulCheckQuestionsSchema = z.tuple([
  z.object({
    id: z.literal("q1"),
    ...questionTextShape,
    options: q1OptionsSchema,
  }),
  z.object({
    id: z.literal("q2"),
    ...questionTextShape,
    options: q2OptionsSchema,
  }),
  z.object({
    id: z.literal("q3"),
    ...questionTextShape,
    options: q3OptionsSchema,
  }),
  z.object({
    id: z.literal("q4"),
    ...questionTextShape,
    options: q4OptionsSchema,
  }),
]);

export type SoulCheckQuestion = z.infer<
  typeof soulCheckQuestionsSchema
>[number];

export const directionOutcomeSchema = z.object({
  direction: z.enum(recommendationCodes),
  label: shortTextSchema,
  roomResponse: shortTextSchema,
  headline: shortTextSchema,
  description: bodyTextSchema,
  invitation: bodyTextSchema,
  /** Shown above the invitation on the result screen. */
  explanation: bodyTextSchema,
});

export type DirectionOutcome = z.infer<typeof directionOutcomeSchema>;

export const directionOutcomesSchema = z
  .array(directionOutcomeSchema)
  .length(recommendationCodes.length)
  .superRefine((outcomes, context) => {
    const directions = outcomes.map(({ direction }) => direction);
    if (new Set(directions).size !== recommendationCodes.length) {
      context.addIssue({
        code: "custom",
        message: "Iedere richting moet precies één uitkomsttekst hebben.",
      });
    }
  });

export const reflectionCardSchema = z.object({
  id: contentIdSchema,
  kind: z.literal("card"),
  title: shortTextSchema,
  message: bodyTextSchema,
  prompt: bodyTextSchema,
  ritual: bodyTextSchema,
  theme: z.enum([
    "stilte",
    "richting",
    "grenzen",
    "verbinding",
    "moed",
    "loslaten",
  ]),
});

export type ReflectionCard = z.infer<typeof reflectionCardSchema>;
export const reflectionCardsSchema = z
  .array(reflectionCardSchema)
  .min(12)
  .superRefine(uniqueIds);

export const dailyRecipeSchema = z.object({
  id: contentIdSchema,
  kind: z.literal("recipe"),
  title: shortTextSchema,
  intro: bodyTextSchema,
  servings: shortTextSchema,
  ingredients: z.array(shortTextSchema).min(2).max(16),
  steps: z.array(bodyTextSchema).min(1).max(10),
  dietaryLabels: z.array(shortTextSchema).max(6),
  reflection: bodyTextSchema,
  note: bodyTextSchema.optional(),
});

export type DailyRecipe = z.infer<typeof dailyRecipeSchema>;
export const dailyRecipesSchema = z
  .array(dailyRecipeSchema)
  .min(8)
  .superRefine(uniqueIds);
export type DailyGift = ReflectionCard | DailyRecipe;

/**
 * Everything about an offer except which direction it belongs to.
 *
 * Price and payment link are editable: Leonie changes her own prices, and
 * waiting on a developer for that is worse than the risk. The guards are that
 * `href` must be https, that the pull refuses a snapshot failing this schema,
 * and that every change still lands as a reviewable git diff.
 *
 * `direction` stays in `offers.ts` — it is wired to scoring, not to commerce.
 */
const offerCopyShape = {
  label: shortTextSchema,
  description: bodyTextSchema,
  ctaLabel: shortTextSchema,
  /** Free text, so "€33", "vanaf €33" and "Gratis" all work. Absent hides it. */
  price: shortTextSchema.optional(),
  href: httpsUrlSchema,
};

export const offerCopySchema = z.tuple([
  z.object({ id: z.literal("special"), ...offerCopyShape }),
  z.object({ id: z.literal("heart-to-heart"), ...offerCopyShape }),
  z.object({ id: z.literal("the-wave"), ...offerCopyShape }),
  z.object({ id: z.literal("substack"), ...offerCopyShape }),
]);

export type OfferCopy = z.infer<typeof offerCopySchema>[number];

export const offersSchema = z.tuple([
  z.object({
    id: z.literal("special"),
    direction: z.literal("special"),
    ...offerCopyShape,
  }),
  z.object({
    id: z.literal("heart-to-heart"),
    direction: z.literal("heart-to-heart"),
    ...offerCopyShape,
  }),
  z.object({
    id: z.literal("the-wave"),
    direction: z.literal("the-wave"),
    ...offerCopyShape,
  }),
  z.object({
    id: z.literal("substack"),
    direction: z.null(),
    ...offerCopyShape,
  }),
]);

export type Offer = z.infer<typeof offersSchema>[number];
export type DirectionOffer = Extract<Offer, { direction: string }>;

/* -------------------------------------------------------------------------
 * Room and interface copy
 *
 * Everything below is authored in the Studio and arrives through the same
 * snapshot as the cards and recipes. The shapes are deliberately fixed: a
 * missing field fails the build, it never becomes a blank spot in the room.
 *
 * Structural facts stay in code and are absent here on purpose — hotspot
 * coordinates (src/config/hotspots.ts), answer codes and scoring (src/lib/),
 * offer links and prices (offers.ts).
 * ---------------------------------------------------------------------- */

export const soulCheckIntroSchema = z.object({
  eyebrow: shortTextSchema,
  title: shortTextSchema,
  description: bodyTextSchema,
  privacy: bodyTextSchema,
  reflectionPrompt: bodyTextSchema,
});

export type SoulCheckIntro = z.infer<typeof soulCheckIntroSchema>;

/**
 * The whole Soul Check block as it arrives from the Studio. Question ids,
 * answer codes and the direction list stay fixed by the tuple schemas above:
 * the editor rewords, the structure cannot drift away from `intake-schema.ts`.
 */
const answerCodes = [
  ...q1Codes,
  ...q2Codes,
  ...q3Codes,
  ...q4Codes,
] as const;

/**
 * One sentence per answer, echoed back on the result screen and in the mail to
 * Leonie. Keyed by the same semantic codes the scoring uses, so the wording is
 * editable without touching what an answer is worth.
 */
export const answerExplanationsSchema = keyedList(
  answerCodes,
  { text: bodyTextSchema },
  "Ieder antwoord moet precies één toelichting hebben.",
);

export type AnswerExplanation = z.infer<typeof answerExplanationsSchema>[number];

export const soulCheckContentSchema = z.object({
  intro: soulCheckIntroSchema,
  questions: soulCheckQuestionsSchema,
  outcomes: directionOutcomesSchema,
  answerExplanations: answerExplanationsSchema,
  methodCopy: bodyTextSchema,
});

const hotspotIds = ["sea", "journal", "hearth", "menu", "boards"] as const;

export const hotspotCopySchema = keyedList(
  hotspotIds,
  {
    label: shortTextSchema,
    shortLabel: shortTextSchema,
    description: shortTextSchema,
    invitation: shortTextSchema,
  },
  "Iedere plek in de kamer moet precies één tekst hebben.",
);

export type HotspotCopy = z.infer<typeof hotspotCopySchema>[number];

const entryIntentIds = ["rest", "direction", "connection", "surprise"] as const;
const seaStateIds = ["calm", "swell", "storm"] as const;
const hearthStepIds = ["arrive", "make-room", "choose"] as const;

const entrySchema = z.object({
  eyebrow: shortTextSchema,
  title: shortTextSchema,
  intro: bodyTextSchema,
  intentLegend: shortTextSchema,
  intents: keyedList(
    entryIntentIds,
    { label: shortTextSchema, detail: shortTextSchema },
    "Iedere intentie moet precies één tekst hebben.",
  ),
  enterLabel: shortTextSchema,
  openingLabel: shortTextSchema,
  openingAnnouncement: shortTextSchema,
  privacyNote: shortTextSchema,
});

const sceneSchema = z.object({
  eyebrow: shortTextSchema,
  title: shortTextSchema,
  description: bodyTextSchema,
  soundLabel: shortTextSchema,
  hotspotLayerLabel: shortTextSchema,
  objectMenuLabel: shortTextSchema,
  objectMenuNavLabel: shortTextSchema,
});

const roomSchema = z.object({
  reactionEyebrow: shortTextSchema,
  reactionContactLabel: shortTextSchema,
  reactions: keyedList(
    recommendationCodes,
    {
      title: shortTextSchema,
      body: bodyTextSchema,
      nextLabel: shortTextSchema,
      announcement: bodyTextSchema,
    },
    "Iedere richting moet precies één kamerreactie hebben.",
  ),
});

const seaSchema = z.object({
  dialogEyebrow: shortTextSchema,
  dialogTitle: shortTextSchema,
  dialogDescription: bodyTextSchema,
  legend: shortTextSchema,
  emptyResponse: bodyTextSchema,
  options: keyedList(
    seaStateIds,
    {
      label: shortTextSchema,
      response: bodyTextSchema,
      nextLabel: shortTextSchema,
    },
    "Iedere zeetoestand moet precies één tekst hebben.",
  ),
});

const journalSchema = z.object({
  dialogEyebrow: shortTextSchema,
  dialogTitle: shortTextSchema,
  dialogDescription: bodyTextSchema,
  returnNoteBefore: shortTextSchema,
  returnNoteAfter: shortTextSchema,
  cardRouteTitle: shortTextSchema,
  cardRouteDetail: shortTextSchema,
  soulCheckRouteTitle: shortTextSchema,
  soulCheckRouteDetail: shortTextSchema,
  privacyNote: bodyTextSchema,
});

const soulCheckPanelSchema = z.object({
  dialogTitle: shortTextSchema,
  resultEyebrow: shortTextSchema,
  whyTitle: shortTextSchema,
  methodSummary: shortTextSchema,
  directionsTitle: shortTextSchema,
  directionsIntro: bodyTextSchema,
  matchLabel: shortTextSchema,
  reflectionOptionalLabel: shortTextSchema,
  reflectionPlaceholder: shortTextSchema,
  shareTitle: shortTextSchema,
  shareBody: bodyTextSchema,
  shareCtaLabel: shortTextSchema,
});

const hearthSchema = z.object({
  dialogTitle: shortTextSchema,
  eyebrow: shortTextSchema,
  title: shortTextSchema,
  intro: bodyTextSchema,
  steps: keyedList(
    hearthStepIds,
    {
      title: shortTextSchema,
      body: bodyTextSchema,
      prompt: bodyTextSchema,
    },
    "Ieder moment bij de haard moet precies één tekst hebben.",
  ),
  invitationEyebrow: shortTextSchema,
  invitationTitle: shortTextSchema,
  invitationBody: bodyTextSchema,
  invitationCtaLabel: shortTextSchema,
});

const dailyGiftSchema = z.object({
  eyebrow: shortTextSchema,
  cardDialogTitle: shortTextSchema,
  recipeDialogTitle: shortTextSchema,
  cardTitle: shortTextSchema,
  recipeTitle: shortTextSchema,
  cardDailyNote: bodyTextSchema,
  recipeDailyNote: bodyTextSchema,
  cardPromptLabel: shortTextSchema,
  ritualTitle: shortTextSchema,
  ingredientsTitle: shortTextSchema,
  stepsTitle: shortTextSchema,
  recipeReflectionLabel: shortTextSchema,
  footerTitle: shortTextSchema,
  footerBody: bodyTextSchema,
});

const boardsSchema = z.object({
  dialogTitle: shortTextSchema,
  eyebrow: shortTextSchema,
  title: shortTextSchema,
  intro: bodyTextSchema,
  matchLabel: shortTextSchema,
  shoreEyebrow: shortTextSchema,
  shoreTitle: shortTextSchema,
});

const contactSchema = z.object({
  dialogEyebrow: shortTextSchema,
  dialogTitle: shortTextSchema,
  dialogDescription: bodyTextSchema,
  eyebrow: shortTextSchema,
  title: shortTextSchema,
  intro: bodyTextSchema,
  shareLegend: shortTextSchema,
  shareAnswersTitle: shortTextSchema,
  shareAnswersDetail: bodyTextSchema,
  shareReflectionTitle: shortTextSchema,
  shareReflectionDetail: bodyTextSchema,
  consentLegend: shortTextSchema,
  contactConsentTitle: shortTextSchema,
  contactConsentDetail: bodyTextSchema,
  newsletterConsentTitle: shortTextSchema,
  newsletterConsentDetail: bodyTextSchema,
  submitLabel: shortTextSchema,
  successTitle: shortTextSchema,
});

const newsletterSchema = z.object({
  dialogEyebrow: shortTextSchema,
  dialogTitle: shortTextSchema,
  dialogDescription: bodyTextSchema,
  eyebrow: shortTextSchema,
  title: shortTextSchema,
  intro: bodyTextSchema,
  consentTitle: shortTextSchema,
  consentDetail: shortTextSchema,
  submitLabel: shortTextSchema,
  successTitle: shortTextSchema,
});

const footerSchema = z.object({
  tagline: shortTextSchema,
  aboutLabel: shortTextSchema,
  newsletterLabel: shortTextSchema,
  privacyLabel: shortTextSchema,
  leaveLabel: shortTextSchema,
  substackLabel: shortTextSchema,
});

const aboutSchema = z.object({
  dialogTitle: shortTextSchema,
  dialogDescription: bodyTextSchema,
  paragraphs: z.array(bodyTextSchema).min(1).max(6),
  linkLabel: shortTextSchema,
  linkHref: httpsUrlSchema,
});

/**
 * Sections rather than Portable Text: the panel already renders a heading and
 * a paragraph per block, and modelling it this way keeps a rich-text renderer
 * out of the browser bundle.
 */
const privacySchema = z.object({
  dialogTitle: shortTextSchema,
  statusNote: bodyTextSchema.optional(),
  sections: z
    .array(z.object({ heading: shortTextSchema, body: bodyTextSchema }))
    .min(1)
    .max(12),
});

export const siteCopySchema = z.object({
  entry: entrySchema,
  scene: sceneSchema,
  room: roomSchema,
  sea: seaSchema,
  journal: journalSchema,
  soulCheckPanel: soulCheckPanelSchema,
  hearth: hearthSchema,
  dailyGift: dailyGiftSchema,
  boards: boardsSchema,
  contact: contactSchema,
  newsletter: newsletterSchema,
  footer: footerSchema,
  about: aboutSchema,
  privacy: privacySchema,
});

export type SiteCopy = z.infer<typeof siteCopySchema>;
